import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import moment from 'moment';

import {
  LevelRewardInput,
  PraSection,
  SetProgressiveAdsMaintenanceMutation,
  SetProgressiveAdsMaintenanceMutationVariables,
  UpdateProgressiveAdsSectionConfigMutation,
  UpdateProgressiveAdsSectionConfigMutationVariables,
} from '../../../../../../__gqltypes__';
import EditRewardsModal from '../../../components/EditRewardsModal';
import { SET_PRA_MAINTENANCE, UPDATE_PRA_SECTION_CONFIG } from '../graphql';
import {
  configToDraft,
  defaultBoxDraft,
  defaultSectionDraft,
  draftToInput,
  getPraSectionDraftWarnings,
  newRandomBoxRow,
  newRewardRow,
  PRA_ALLOWED_REWARD_TYPES,
  PRA_SECTION_LABELS,
  PraBoxDraft,
  PraRewardRow,
  PraSectionConfigData,
  PraSectionDraft,
  validatePraSectionDraft,
} from '../utils';
import { PraCooldownEditor } from './PraCooldownEditor';
import { PraMaintenancePanel } from './PraMaintenancePanel';
import { PraMilestonesTable } from './PraMilestonesTable';
import { PraRandomBoxEditor } from './PraRandomBoxEditor';
import { PraSaveConfirmModal } from './PraSaveConfirmModal';

type Props = {
  section: PraSection;
  initialConfig: PraSectionConfigData | null;
};

// One section's editor card (FREE / CHAINED — NOT an event list: a singleton config per
// namespace). Fully self-managing after the initial load: both mutations return the fresh
// config, so the card never refetches. A maintenance flip only refreshes the server state —
// unsaved draft edits survive it (maintenance is an optional switch while editing; no waiting rule).
export function PraSectionCard({ section, initialConfig }: Props): JSX.Element {
  const [serverConfig, setServerConfig] = useState<PraSectionConfigData | null>(initialConfig);
  const [draft, setDraft] = useState<PraSectionDraft>(() =>
    initialConfig ? configToDraft(initialConfig) : defaultSectionDraft(section)
  );
  const [editRewardIndex, setEditRewardIndex] = useState<number | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [serverWarnings, setServerWarnings] = useState<string[]>([]);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // The only prop-driven reset: the parent's query (re)load.
  useEffect(() => {
    setServerConfig(initialConfig);
    setDraft(initialConfig ? configToDraft(initialConfig) : defaultSectionDraft(section));
  }, [initialConfig, section]);

  const [updateConfig, { loading: saving }] = useMutation<
    UpdateProgressiveAdsSectionConfigMutation,
    UpdateProgressiveAdsSectionConfigMutationVariables
  >(UPDATE_PRA_SECTION_CONFIG, {
    onCompleted: ({ updateProgressiveAdsSectionConfig: payload }) => {
      setServerConfig(payload.config);
      setDraft(configToDraft(payload.config));
      setServerWarnings(payload.warnings);
      setMutationError(null);
    },
    onError: (error) => setMutationError(error.message),
  });

  const [setMaintenance, { loading: maintenanceSaving }] = useMutation<
    SetProgressiveAdsMaintenanceMutation,
    SetProgressiveAdsMaintenanceMutationVariables
  >(SET_PRA_MAINTENANCE, {
    onCompleted: ({ setProgressiveAdsMaintenance: payload }) => {
      // Server state only — the draft (possibly mid-edit) is deliberately untouched.
      setServerConfig(payload.config);
      setMutationError(null);
    },
    onError: (error) => setMutationError(error.message),
  });

  const validationErrors = useMemo(() => validatePraSectionDraft(draft), [draft]);
  const draftWarnings = useMemo(() => getPraSectionDraftWarnings(draft), [draft]);
  const canSave = validationErrors.length === 0;

  const handleToggleMaintenance = useCallback(
    (enabled: boolean) => {
      if (!serverConfig) {
        return;
      }
      if (
        enabled &&
        !window.confirm(
          `Enable maintenance for the ${PRA_SECTION_LABELS[section]} section? ` +
            'Players without an active cycle will see the maintenance screen (and cannot start a new cycle) ' +
            'until you turn it off. Mid-cycle players are unaffected.'
        )
      ) {
        return;
      }
      setMaintenance({ variables: { input: { section, enabled } } });
    },
    [serverConfig, section, setMaintenance]
  );

  const handleConfirmSave = useCallback(() => {
    setShowSaveConfirm(false);
    setServerWarnings([]);
    updateConfig({ variables: { input: draftToInput(draft) } });
  }, [draft, updateConfig]);

  const handleEditRewardSave = useCallback(
    (newReward: LevelRewardInput) => {
      if (editRewardIndex === null) {
        return;
      }
      setDraft((prev) => ({
        ...prev,
        rewards: prev.rewards.map((row, index) =>
          index === editRewardIndex ? { ...row, reward: { ...newReward } } : row
        ),
      }));
      setEditRewardIndex(null);
    },
    [editRewardIndex]
  );

  const handleChangeRows = useCallback((rows: PraRewardRow[]) => {
    setDraft((prev) => ({ ...prev, rewards: rows }));
  }, []);

  // Box entry ⇔ box config, kept consistent in one place. FREE has exactly one entry
  // (D12), so its box replaces the single reward; CHAINED appends the locked final row.
  const handleToggleBox = useCallback((enabled: boolean) => {
    setDraft((prev) => {
      if (enabled) {
        const boxRow = newRandomBoxRow();
        const rewards =
          prev.section === PraSection.FREE ? [boxRow] : [...prev.rewards.filter((row) => !row.isRandomBox), boxRow];
        return { ...prev, rewards, randomBox: prev.randomBox ?? defaultBoxDraft() };
      }
      const rewards = prev.rewards.filter((row) => !row.isRandomBox);
      return {
        ...prev,
        rewards: rewards.length === 0 ? [newRewardRow()] : rewards,
        randomBox: null,
      };
    });
  }, []);

  const handleChangeBox = useCallback((boxDraft: PraBoxDraft) => {
    setDraft((prev) => ({ ...prev, randomBox: boxDraft }));
  }, []);

  const busy = saving || maintenanceSaving;

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{PRA_SECTION_LABELS[section]}</strong>{' '}
          <Badge variant={draft.enabled ? 'success' : 'secondary'}>{draft.enabled ? 'ENABLED' : 'DISABLED'}</Badge>
          {serverConfig?.maintenance && (
            <Badge variant="danger" className="ml-2">
              MAINTENANCE
            </Badge>
          )}
        </div>
        <span className="text-muted small">
          {serverConfig
            ? `namespace: ${serverConfig.namespace} · updated ${moment(serverConfig.updatedAt).format(
                'YYYY-MM-DD HH:mm'
              )}`
            : 'not configured yet'}
        </span>
      </Card.Header>
      <Card.Body>
        <Form.Group>
          <Form.Check
            type="switch"
            id={`pra-enabled-${section}`}
            label="Enabled (the section is visible and claimable for players)"
            checked={draft.enabled}
            onChange={(e) => {
              const { checked } = e.target;
              setDraft((prev) => ({ ...prev, enabled: checked }));
            }}
          />
        </Form.Group>
        <PraCooldownEditor
          cooldownSeconds={draft.cooldownSeconds}
          onChange={(seconds) => setDraft((prev) => ({ ...prev, cooldownSeconds: seconds }))}
        />
        <PraMaintenancePanel
          section={section}
          configExists={serverConfig !== null}
          maintenance={serverConfig?.maintenance ?? false}
          maintenanceSince={serverConfig?.maintenanceSince}
          saving={maintenanceSaving}
          onToggle={handleToggleMaintenance}
        />
        <Form.Label className="font-weight-bold">
          {section === PraSection.FREE ? 'Reward (exactly one — GD invariant)' : 'Milestones (claimed in order)'}
        </Form.Label>
        <PraMilestonesTable
          rows={draft.rewards}
          canEditStructure={section === PraSection.CHAINED}
          onEditReward={setEditRewardIndex}
          onChangeRows={handleChangeRows}
        />
        <PraRandomBoxEditor
          section={section}
          hasBox={draft.rewards.some((row) => row.isRandomBox)}
          box={draft.randomBox}
          onToggleBox={handleToggleBox}
          onChangeBox={handleChangeBox}
        />
        {draftWarnings.length > 0 && (
          <Alert variant="warning">
            <Alert.Heading as="h6">Warnings (do not block saving):</Alert.Heading>
            <ul className="mb-0">
              {draftWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}
        {serverWarnings.length > 0 && (
          <Alert variant="warning" dismissible onClose={() => setServerWarnings([])}>
            <Alert.Heading as="h6">Saved with warnings (non-blocking):</Alert.Heading>
            <ul className="mb-0">
              {serverWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}
        {mutationError && (
          <Alert variant="danger" dismissible onClose={() => setMutationError(null)}>
            {mutationError}
          </Alert>
        )}
        {validationErrors.length > 0 && (
          <Alert variant="danger">
            <Alert.Heading as="h6">Fix the following before saving:</Alert.Heading>
            <ul className="mb-0">
              {validationErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        <div className="d-flex justify-content-end">
          <Button variant="primary" disabled={!canSave || busy} onClick={() => setShowSaveConfirm(true)}>
            {saving ? 'Saving…' : `Save ${PRA_SECTION_LABELS[section]}`}
          </Button>
        </div>
      </Card.Body>
      <EditRewardsModal
        isEdit={true}
        tier={null}
        level={0}
        show={editRewardIndex !== null}
        reward={editRewardIndex !== null ? draft.rewards[editRewardIndex]?.reward ?? null : null}
        handleSave={handleEditRewardSave}
        handleClose={() => setEditRewardIndex(null)}
        allowedRewardTypes={PRA_ALLOWED_REWARD_TYPES}
      />
      <PraSaveConfirmModal
        show={showSaveConfirm}
        section={section}
        isNewConfig={serverConfig === null}
        maintenance={serverConfig?.maintenance ?? false}
        onCancel={() => setShowSaveConfirm(false)}
        onConfirm={handleConfirmSave}
      />
    </Card>
  );
}
